import { type PermissionAuditEntry, formatPersonName } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { useApiResource } from '#client/hooks/useResourceItem';
import { formatDateTime } from '#client/lib/format';
import { describePermissionChange } from './permission-history.logic';

/**
 * Who changed what a role or a person may do, newest first.
 *
 * Kept because a permission is the kind of thing nobody remembers granting:
 * "why can she approve orders?" deserves an answer with a name and a date.
 */
export function PermissionHistory({ path }: { path: string }) {
  const history = useApiResource<PermissionAuditEntry[]>(path);
  const entries = history.data ?? [];

  return (
    <Card title="Change history" subtitle="The last 50 changes, newest first.">
      {history.error && <p className="c-perm-history__empty">{history.error}</p>}
      {!history.loading && entries.length === 0 && !history.error && (
        <p className="c-perm-history__empty">No changes recorded yet.</p>
      )}
      {entries.length > 0 && (
        <ol className="c-perm-history">
          {entries.map((entry) => (
            <li key={entry.id} className="c-perm-history__item">
              <span className="c-perm-history__when">{formatDateTime(entry.created_at)}</span>
              <span className="c-perm-history__who">{formatPersonName(entry.actor)}</span>
              <span className="c-perm-history__what">{describePermissionChange(entry)}</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
